import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getWebApp, haptic } from '@/lib/telegram';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bot, ExternalLink, CheckCircle2, Loader2, XCircle, Zap } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { PartnerBot } from '@/api/types';
import { toast } from 'sonner';

const FALLBACK_USERNAME = import.meta.env.VITE_FRANCHISE_BOT_USERNAME || '';

// skeleton-ok: мастер создания бота. Вёрстка статичная, запросы фоновые:
// имя франш-бота (есть fallback) и polling списка ботов ради авто-перехода.
export const Route = createFileRoute('/onboarding/bot')({ component: WizardBot });

type ValidateResult = { ok: true; bot: { id: string; username: string } } | { ok: false; message: string };

function WizardBot() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [token, setToken] = useState('');

  const { mutate: validate, data: result, isPending, reset } = useMutation({
    mutationFn: async (raw: string): Promise<ValidateResult> => {
      // Токен из BotFather копируется вместе с пробелом или переводом строки —
      // особенно на мобиле. Якорная регулярка отвергала такой токен, и партнёр
      // на первом же шаге получал «формат неправильный» по верному токену.
      const tokenStr = raw.trim();
      if (!/^\d{6,12}:[A-Za-z0-9_-]{30,}$/.test(tokenStr)) {
        return { ok: false, message: 'Формат токена выглядит неправильно' };
      }
      try {
        const bot = await api.createBot({ token: tokenStr });
        return { ok: true, bot: { id: bot.id, username: bot.username } };
      } catch (err) {
        const msg =
          err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body
            ? (err.body as { error?: { message?: string } }).error?.message ?? 'Бот не зарегистрирован'
            : 'Не удалось подтвердить токен';
        return { ok: false, message: msg };
      }
    },
    onSuccess: (r) => {
      haptic(r.ok ? 'success' : 'error');
      if (r.ok) {
        qc.invalidateQueries({ queryKey: ['bots'] });
      }
    },
  });

  const isValid = result?.ok === true;
  const createdBotId = result?.ok ? result.bot.id : null;

  /* Токен всегда именно вставляют, а не набирают: на мобиле это долгий тап по
   * полю, который в Telegram-вебвью то и дело перехватывается выделением. Явная
   * кнопка убирает эту возню.
   *
   * Кнопку показываем всегда, а не только при наличии Clipboard API. Пряталка
   * по наличию API приводила к тому, что в Telegram кнопки просто не было и
   * партнёр о ней не узнавал. Отсутствие буфера отрабатывается в обработчике
   * подсказкой «вставьте вручную» — это честнее молчаливого исчезновения. */

  const pasteToken = (): void => {
    let settled = false;

    /* Подсказка по таймеру, но она НЕ отменяет чтение буфера. Две причины
     * держать таймер: readText() умеет висеть вечно, если разрешение застряло
     * в состоянии «prompt», а показать запрос вебвью негде (проверено — промис
     * жив и через 3 секунды); и наоборот, запрос могут показать, и человек
     * думает над ним секунды. Гонка, которая отбрасывала поздний ответ, во
     * втором случае теряла бы вставку ровно тогда, когда доступ разрешили. */
    const hint = setTimeout(() => {
      if (!settled) toast('Разрешите доступ к буферу — или вставьте вручную');
    }, 2500);

    let reading: Promise<string>;
    try {
      reading = navigator.clipboard.readText();
    } catch {
      clearTimeout(hint);
      toast.error('Не удалось прочитать буфер — вставьте токен вручную');
      return;
    }

    reading
      .then((text) => {
        settled = true;
        clearTimeout(hint);
        const clean = text.trim();
        if (!clean) {
          toast.error('Буфер обмена пуст');
          return;
        }
        setToken(clean);
        if (result) reset();
        haptic('success');
      })
      .catch(() => {
        settled = true;
        clearTimeout(hint);
        toast.error('Не удалось прочитать буфер — вставьте токен вручную');
      });
  };

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton || !tg?.MainButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/' });
    tg.BackButton.onClick(onBack);

    if (isValid && createdBotId) {
      tg.MainButton.setText('Открыть бота');
      tg.MainButton.show();
      // Второй шаг мастера: готовое оформление. Его можно пропустить, там же кнопка.
      const onMain = () =>
        navigate({ to: '/bot/templates', search: { bot: createdBotId, from: 'onboarding' } });
      tg.MainButton.onClick(onMain);
      return () => {
        tg.MainButton.offClick(onMain);
        tg.MainButton.hide();
        tg.BackButton.offClick(onBack);
      };
    }
    tg.MainButton.hide();
    return () => tg.BackButton.offClick(onBack);
  }, [isValid, createdBotId, navigate, t]);

  const openBotFather = () => {
    const tg = getWebApp();
    tg?.openTelegramLink('https://t.me/BotFather');
  };

  // Username франшизного manager-бота — для t.me/newbot deeplink.
  // Приоритет: runtime ответ /api/partner/me → build-time env → пусто.
  // staleTime:0 чтобы не показать кэш-снапшот, у которого поля ещё нет.
  const { data: me } = useQuery({
    queryKey: ['partner-me'],
    queryFn: () => api.me(),
    staleTime: 0,
  });
  const franchiseBotUsername = me?.franchiseBotUsername || FALLBACK_USERNAME;

  // Polling списка ботов — если партнёр воспользовался t.me/newbot и backend
  // обработал managed_bot update, новый бот появится здесь.
  const { data: existingBots } = useQuery({
    queryKey: ['bots'],
    queryFn: () => api.bots(),
    refetchInterval: 3000,
  });
  const [snapshotIds] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!existingBots) return;
    if (snapshotIds.size === 0) {
      existingBots.forEach((b) => snapshotIds.add(b.id));
      return;
    }
    const fresh = existingBots.find((b) => !snapshotIds.has(b.id));
    if (fresh) {
      haptic('success');
      qc.invalidateQueries({ queryKey: ['bots'] });
      navigate({ to: '/bot/templates', search: { bot: fresh.id, from: 'onboarding' } });
    }
  }, [existingBots, snapshotIds, qc, navigate]);

  const openInstantCreate = async () => {
    const tg = getWebApp();
    if (franchiseBotUsername) {
      // Просим бэк отправить нам в чат с manager-ботом сообщение с inline-кнопкой
      // (как делает /addbot в самом боте). Inline-кнопка с /newbot/<m>/
      // открывает managed-bot creation нативно — без браузера и popup'ов.
      try {
        await api.instantBotPrompt();
        haptic('success');
        tg?.close();
        return;
      } catch {
        toast.error('Не удалось подготовить мгновенное создание — открываем BotFather');
        // fall through to fallback
      }
    }
    tg?.openTelegramLink('https://t.me/BotFather?start=newbot');
    toast.info('Открыли BotFather. Пройдите шаги и вставьте токен в форму ниже');
  };

  return (
    <div className="p-4 space-y-6 pb-24">
      <div className="px-2 mt-2">
        <div className="w-14 h-14 bg-white/[0.07] text-foreground rounded-2xl flex items-center justify-center mb-4">
          <Bot className="w-7 h-7" />
        </div>
        <h2 className="text-2xl font-bold tracking-tight mb-1">{t('Wizard 1 title')}</h2>
        <p className="text-sm text-muted-foreground">Шаг 1 из 2</p>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <Step number={1} text={t('Wizard 1 step 1')} />
          <Step number={2} text={t('Wizard 1 step 2')} />
          <Step number={3} text={t('Wizard 1 step 3')} />
          <Step number={4} text={t('Wizard 1 step 4')} />
        </CardContent>
      </Card>

      <button
        type="button"
        onClick={openInstantCreate}
        className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
      >
        <Zap className="w-4 h-4" />
        Создать бота мгновенно
      </button>

      <Button variant="secondary" className="w-full h-12" onClick={openBotFather}>
        <ExternalLink className="w-4 h-4" />
        {t('Open BotFather')}
      </Button>
      <p className="text-[11px] text-muted-foreground text-center -mt-2">
        «Мгновенно» — Telegram сам создаст бот и подключит его к платформе.
        Через BotFather: классический способ, скопируйте токен и вставьте ниже.
      </p>

      <div className="bg-card border border-white/5 rounded-2xl p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <label htmlFor="bot-token" className="text-sm font-semibold">
            {t('Bot token')}
          </label>
          <button
            type="button"
            onClick={pasteToken}
            className="text-xs text-foreground px-2 py-1 -mr-2 rounded-lg active:scale-95 transition-transform"
          >
            Вставить
          </button>
        </div>
        <input
          id="bot-token"
          value={token}
          onChange={(e) => {
            setToken(e.target.value);
            if (result) reset();
          }}
          /* Токен — не текст на языке: автозамена и заглавные его ломают. */
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
          className="w-full bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-white/30 text-foreground placeholder-muted-foreground"
        />
        <button
          type="button"
          onClick={() => validate(token)}
          disabled={isPending || !token}
          className="w-full bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
        >
          {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          {t('Validate')}
        </button>

        {result && !result.ok && (
          <div className="flex items-center gap-2 text-red-500 text-sm">
            <XCircle className="w-4 h-4 shrink-0" />
            <span className="break-words">{result.message}</span>
          </div>
        )}
        {result?.ok && (
          <div className="flex items-center gap-2 text-green-500 text-sm">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            {t('Token ok', { username: result.bot.username })}
          </div>
        )}
      </div>

      {/* Бот с клиентами уже есть на другой платформе: новый бот здесь был бы ошибкой,
          клиенты остались бы в старом. Это редкий случай, поэтому ссылка, а не третья
          равная карточка: иначе «Переезд» жали те, кому переезжать неоткуда.
          Переезд только у владельца кабинета, делегату бэк ответит 403. */}
      {!me?.isDelegate && (
        <p className="px-1 pt-1 text-center text-[13px] text-dim leading-snug">
          Уже есть бот с клиентами на другой платформе?{' '}
          <button
            type="button"
            onClick={() => {
              haptic('light');
              navigate({ to: '/bot-import' });
            }}
            className="font-semibold text-foreground underline underline-offset-4 decoration-white/30"
          >
            Переехать
          </button>
        </p>
      )}
    </div>
  );
}

function Step({ number, text }: { number: number; text: string }) {
  return (
    <div className="flex gap-3 items-start">
      <div className="w-7 h-7 bg-white/[0.07] text-foreground rounded-lg flex items-center justify-center font-bold text-sm shrink-0">
        {number}
      </div>
      <span className="text-sm leading-relaxed pt-0.5">{text}</span>
    </div>
  );
}
